import React from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { AssetContainer, HTMLAsset } from '@techstyle/react-assets';
import { Desktop } from '@techstyle/react-components-legacy';
import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { toStringSet } from '../../utils/assets';
import { useGshop } from '../../utils/gshop';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { promoPriceHiddenPromoTypes } from '../../utils/promos';
import { Component as AfterpayMessaging } from '../AfterpayMessaging';
import { Component as Price } from '../ExtendedPrice';
import KlarnaMessaging from '../KlarnaMessaging/KlarnaMessaging';
import PriceChooser from '../PriceChooser/PriceChooser';
import { Component as ProductDetailFlag } from '../ProductDetailFlag';
import { useProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import { Component as ProductDetailName } from '../ProductDetailName';
import { Container as ProductDetailStars } from '../ProductDetailStars';
import { ProductPrice } from '../ProductPrice/ProductPrice';
import { Component as WishlistButton } from '../WishlistButton';

const firstSetBundlePromoFpl = config.get(
  'public.productTags.firstSetBundlePromo'
);

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.moderate}px;

  ${desktop`
    border-bottom: 2px solid ${props => props.theme.colors.gray150};
    padding-bottom: ${props => props.theme.spacing.moderate}px;
  `}

  ${mobile`
    padding: ${props => props.theme.spacing.small}px;
  `}
`;

const PromoAsset = styled(HTMLAsset)`
  ${({ $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled &&
    css`
      ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles}
    `}
`;

const StyledWishlistButton = styled(WishlistButton)`
  height: 20px;
  width: 20px;
  flex: 0 0 20%;
  margin: 0 0 ${props => props.theme.spacing.small}px 0;
  text-align: right;

  ${desktop`
    order: 2;
    margin-bottom: 0;
  `};
`;

const getPromoColors = (theme, isClearanceItem, isVip) => {
  // TODO: Are we going to use different colors based on promo type/membership status?
  // We aren't right now, but can keep this here if we want to add that logic later.
  // if (isVip && isClearanceItem) {
  //   return {
  //     background: theme.colors.error,
  //     color: theme.colors.white,
  //   };
  // }

  // if (isVip) {
  //   return {
  //     background: theme.colors.black,
  //     color: theme.colors.black,
  //   };
  // }

  return {
    // background: theme.colors.memberOffers,
    color: theme.colors.memberOffers,
  };
};

const PromoTextWrapper = styled.div`
  width: 100%;
  display: flex;
  text-align: center;
  justify-content: space-between;
  font-weight: 400;

  ${({ $gridPdpPricingUpdatesEnabled, theme, $isClearanceItem, $isVip }) => {
    if ($gridPdpPricingUpdatesEnabled) {
      const { color } = getPromoColors(theme, $isClearanceItem, $isVip);

      return css`
        color: ${color};

        ${BundleSeparatorMessage} {
          ${theme.paragraph.variants.paragraph4Uppercase.textStyles}
          font-weight: 400;
          align-self: center;
        }

        ${BundleFirstSetPriceWrapper} {
          ${PromoText} {
            ${theme.subHeader.variants.subHeading3Uppercase.textStyles}
          }
        }

        ${StyledPrice}, ${StyledPromoPrice}, ${PromoText} {
          ${theme.subHeader.variants.subHeading3Uppercase.textStyles}
          color: ${color};
        }

        ${PromoText} {
          ${theme.paragraph.variants.paragraph4Uppercase.textStyles}
          color: ${color};
        }
      `;
    }
  }}
`;

const PriceWrapper = styled.div`
  display: flex;
  flex: 0 0 100%;
  max-width: 100%;
  flex-wrap: wrap;
  ${desktop`
    margin-top: ${props => props.theme.spacing.small}px;
    order: 4;
  `};

  ${props => props.theme.paragraph.variants.paragraph3Uppercase.textStyles}

  .afterpay-paragraph {
    order: 6;

    margin-top: ${props => props.theme.spacing.tiny}px;
    margin-left: -${props => props.theme.spacing.large}px;
    padding: 4px ${props => props.theme.spacing.tiny}px 4px
      ${props => props.theme.spacing.large}px;
    ${props => (props.shouldHideAfterpay ? `display: none;` : null)}
  }
`;

const PromoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex: 0 0 100%;
  justify-content: space-between;
  white-space: nowrap;

  ${desktop`
    order: 4;
  `};
`;

const PromoText = styled.div`
  ${props => props.nowrap && 'white-space: nowrap;'}
  ${({ $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled &&
    css`
      ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles}
    `}
`;

const PriceContainer = styled.div`
  width: 100%;
  ${desktop`
    order: 5;
  `};
`;

const ProductPriceSpan = styled.div`
  align-items: center;
  color: ${props => props.theme.colors.default};
  display: flex;
  flex-wrap: wrap;
  justify-content: ${props =>
    props.isBorderFreeOrDowngradeUser ? 'flex-start' : 'space-between'};
  align-items: baseline;

  ${({
    $gridPdpPricingUpdatesEnabled,
    $isVip,
    $shouldShowPromoStyling,
    theme,
  }) => {
    const priceColor =
      $isVip && $shouldShowPromoStyling ? theme.colors.memberOffers : null;

    if ($gridPdpPricingUpdatesEnabled) {
      return css`
        ${theme.paragraph.variants.paragraph4Uppercase.textStyles}

        ${StyledPrice} {
          ${theme.paragraph.variants.paragraph3Uppercase.textStyles}
        }
        ${priceColor &&
        css`
          ${StyledMessage}, ${StyledPrice} {
            color: ${priceColor};
          }

          ${StyledPrice} {
            ${theme.subHeader.variants.subHeading3Uppercase.textStyles}
          }
        `}
      `;
    }

    return css`
      margin-top: 4px;
    `;
  }}

  ${desktop`
    text-align: left;
  `};
`;

const PriceItem = styled(ProductPriceSpan)`
  font-weight: normal;
`;

const StyledMessage = styled.div`
  color: ${props =>
    props.shouldGreyedOutV2
      ? props.theme.colors.grey
      : props.theme.colors.black};
`;

const StyledPrice = styled(Price)`
  padding-left: ${props => (props.solo ? '4' : '10')}px;

  color: ${props =>
    props.shouldGreyedOutV2
      ? props.theme.colors.grey
      : props.theme.colors.black};
`;

const StyledRegPrice = styled(StyledPrice)`
  padding-left: 4px;
`;

const StyledPromoPrice = styled(Price)`
  padding-left: ${props => (props.pdpSingleCTAFlagEnabled ? '30px' : '6px')};
  white-space: ${props =>
    props.pdpSingleCTAFlagEnabled ? 'nowrap' : 'normal'};
  color: ${props => props.theme.colors.black};

  ${({ $isHighlightedPromo, $gridPdpPricingUpdatesEnabled }) =>
    $isHighlightedPromo &&
    $gridPdpPricingUpdatesEnabled &&
    css`
      color: ${props => props.theme.colors.white};
    `}
`;

const BundleFirstSetWrapper = styled.div`
  display: flex;
  flex-direction: ${props => (props.showAsColumn ? 'column' : 'row')};
  align-items: end;
`;

const BundleFirstSetPriceWrapper = styled.div`
  display: flex;
  gap: 3px;
`;

const BundleSeparatorMessage = styled.span``;

const BogoPromoWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;

  ${StyledPromoPrice} {
    padding-left: 0;
  }
`;

const StyledAdditionalDiscountText = styled.span`
  font-size: 10px;
`;

const StyledProductDetailStars = styled(ProductDetailStars)`
  ${props => props.theme.subHeader.variants.subHeading2.textStyles}
  align-self: flex-start;
  margin: ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.small}px 0 0;
  order: 3;
`;

const StyledProductDetailFlag = styled(ProductDetailFlag)`
  align-self: flex-end;
  margin-bottom: 0;
  margin-top: ${props => props.theme.spacing.small}px;
  order: 3;
`;

const USDPrice = styled.div`
  font-size: 16px;
  padding-left: 4px;
  span {
    padding-left: 4px;
  }
`;

const PromotionalMessage = styled.div`
  order: 5;
  margin-top: 8px;
  color: ${props => props.theme.colors.memberOffers};
  font-weight: bold;
`;

const afterpayClassName = 'afterpay-price';

class ProductDetailHeader extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    children: PropTypes.string,
    isBorderfreeCustomer: PropTypes.bool,
    isInfluencer: PropTypes.bool,
    isPreorderable: PropTypes.bool,
    isProductCustomizable: PropTypes.bool,
    isProductSet: PropTypes.bool,
    isVip: PropTypes.bool,
    isVipCart: PropTypes.bool,
    loadProfile: PropTypes.func,
    product: PropTypes.shape({
      retail_unit_price: PropTypes.number,
      bundle_separate_retail_unit_price: PropTypes.number,
      default_unit_price: PropTypes.number,
      featured_product_location_id_list: PropTypes.arrayOf(PropTypes.number),
      promos: PropTypes.shape({
        promo_label: PropTypes.string,
        promo_price: PropTypes.number,
        display_on_pdp: PropTypes.number,
      }),
      master_product_id: PropTypes.number,
      /* Array of sizes for current/selected product */
      product_id_object_list: PropTypes.arrayOf(
        PropTypes.shape({
          available_quantity: PropTypes.number,
          label_instance: PropTypes.string,
          size: PropTypes.string,
        })
      ),
      /* Product Sets have nested products */
      product_in_sets: PropTypes.arrayOf(
        PropTypes.shape({
          product: PropTypes.shape({
            master_product_id: PropTypes.number,
            product_id_object_list: PropTypes.array,
            related_product_id_object_list: PropTypes.array,
          }),
        })
      ),
      /* Array of colors with corresponding product ids */
      related_product_id_object_list: PropTypes.arrayOf(
        PropTypes.shape({
          /* Array of sizes for color */
          product_id_object_list: PropTypes.array,
          related_product_id: PropTypes.number,
        })
      ),
    }),
    promos: PropTypes.object,
    shouldShowPromoPrice: PropTypes.bool,
    isClearanceItem: PropTypes.bool.isRequired,
    additionalDiscounts: PropTypes.object,
    showPriceSelectors: PropTypes.bool,
    pdpSingleCTAFlagEnabled: PropTypes.bool,
    isKlarnaEnabled: PropTypes.bool,
    showPromotionalMessage: PropTypes.bool,
  };

  static defaultProps = {
    isClf: false,
    isPreorderable: false,
    isProductSet: false,
    isVip: false,
    isVipCart: false,
    forceShowPreorder: false,
    signInModalIsOpen: false,
    isClearanceItem: false,
    shouldHideReviewStars: false,
    shouldHideFavButton: false,
    product: {},
    loadProfile: () => {},
  };

  state = {
    hidePromo: true,
  };

  componentDidMount() {
    const { loadProfile } = this.props;
    loadProfile();
    this.setState({ hidePromo: false });
  }

  renderPromoHeader() {
    // for promo header, there are 3 scenarios
    // 1. for all clearance items, we are showing promo_label on the left, and promo_price on the right
    // 2. for bundle from asset contianer, htmlText on the left, and pdpLabel on the right
    // 3. for cartpromo from asset container, pdp lable on the left.
    // 4. for other promos with promo prices, pdpLabel or promo_label on the left, promo price on the right
    // when we are getting promo in product and cartpromo from AssetContainer at the same time, we want to priority product promo first.

    const {
      promos,
      isClearanceItem,
      isInfluencer,
      shouldShowPromoPrice,
      product,
      isVip,
      isVipCart,
      shouldDisplayNewProductPricing,
      pdpSingleCTAFlagEnabled,
      gridPdpPricingUpdatesEnabled,
    } = this.props;

    const { default_unit_price: defaultPrice, retail_unit_price: retailPrice } =
      product;

    const { promo_type_id: promoTypeId } = promos ?? {};

    // We don't want to show the promo price for some promo types like BOGO
    const hasHiddenPricePromo = promoPriceHiddenPromoTypes.has(promoTypeId);

    if (isClearanceItem && promos.promo_price) {
      return (
        <PromoTextWrapper
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
          $isClearanceItem={isClearanceItem}
          $isVip={isVip}
        >
          <PromoText isClearanceItem>{promos.promo_label}</PromoText>
          <StyledPromoPrice
            amount={promos.promo_price}
            isClearanceItem={isClearanceItem}
            pdpSingleCTAFlagEnabled={pdpSingleCTAFlagEnabled}
          />
        </PromoTextWrapper>
      );
    }

    const promoAssetFilter = () => {
      return asset => {
        const hasFplId = product?.featured_product_location_id_list.includes(
          asset?.options?.customVars?.fplid
        );

        if (hasFplId && hasHiddenPricePromo) {
          const { flags } = asset.options.customVars;
          const formattedFlags = toStringSet(flags, true);

          return formattedFlags.has('bundle');
        }

        return hasFplId;
      };
    };

    return (
      // FIXME: We should simplify the promo display logic in this section, it's a bit messy and
      // required an early return for handling BOGO promos properly.
      <AssetContainer name="pdp_html_fpl" filter={promoAssetFilter}>
        {asset => {
          let pdpLabelForPromoPrice;
          let formattedflags;

          if (hasHiddenPricePromo) {
            const productPrice =
              isVip || isVipCart ? defaultPrice : retailPrice;
            const { pdpLabel } = asset?.data?.options.customVars ?? {};
            const promoText = promos.promo_label;
            return (
              <PromoTextWrapper
                $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                $isVip={isVip}
              >
                <BogoPromoWrapper>
                  <StyledPromoPrice
                    amount={productPrice}
                    shouldGreyedOut
                    pdpSingleCTAFlagEnabled={pdpSingleCTAFlagEnabled}
                  />
                  <FormattedMessage id="site_pdp.or" defaultMessage="or" />
                  <PromoText
                    data-autotag="pdp-non-vip-promo"
                    dangerouslySetInnerHTML={{ __html: pdpLabel ?? promoText }}
                  />
                </BogoPromoWrapper>
              </PromoTextWrapper>
            );
          }

          if (asset.data && !isInfluencer) {
            const { pdpLabel, flags, fplid, overlayText } =
              asset.data.options.customVars;
            pdpLabelForPromoPrice = pdpLabel;
            formattedflags = toStringSet(flags, true);
            const hasBundle = formattedflags.has('bundle');

            if (hasBundle) {
              let condition = fplid !== firstSetBundlePromoFpl && !overlayText;

              let promoPriceAmout =
                overlayText && promos.display_on_pdp
                  ? promos.promo_price
                  : defaultPrice;

              let promoText =
                pdpLabel || overlayText
                  ? { __html: overlayText || pdpLabel }
                  : null;

              if (this.props.isVip) {
                condition = fplid !== firstSetBundlePromoFpl;

                promoPriceAmout = defaultPrice;

                promoText = pdpLabel ? { __html: pdpLabel } : null;
              }

              return condition ? (
                <PromoTextWrapper
                  $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  $isVip={isVip}
                >
                  <PromoAsset
                    asset={asset.data}
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  />
                  <BundleFirstSetPriceWrapper
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  >
                    <PromoText
                      data-autotag="pdp-non-vip-promo"
                      dangerouslySetInnerHTML={
                        pdpLabel ? { __html: pdpLabel } : null
                      }
                      $gridPdpPricingUpdatesEnabled={
                        gridPdpPricingUpdatesEnabled
                      }
                    />
                  </BundleFirstSetPriceWrapper>
                </PromoTextWrapper>
              ) : (
                <PromoTextWrapper
                  $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  $isVip={isVip}
                >
                  <PromoAsset
                    asset={asset.data}
                    $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  />
                  <BundleFirstSetWrapper
                    showAsColumn={pdpSingleCTAFlagEnabled && !isVipCart}
                  >
                    {pdpSingleCTAFlagEnabled && !isVipCart ? (
                      <>
                        <div>
                          <StyledPromoPrice
                            amount={promoPriceAmout}
                            shouldGreyedOut
                            pdpSingleCTAFlagEnabled={pdpSingleCTAFlagEnabled}
                          />
                          &nbsp;
                          <FormattedMessage
                            id="site_pdp.or"
                            defaultMessage="or"
                          />
                        </div>
                        <BundleFirstSetPriceWrapper
                          $gridPdpPricingUpdatesEnabled={
                            gridPdpPricingUpdatesEnabled
                          }
                        >
                          <PromoText
                            data-autotag="pdp-non-vip-promo"
                            dangerouslySetInnerHTML={promoText}
                            $gridPdpPricingUpdatesEnabled={
                              gridPdpPricingUpdatesEnabled
                            }
                          />
                        </BundleFirstSetPriceWrapper>
                      </>
                    ) : (
                      <BundleFirstSetPriceWrapper
                        $gridPdpPricingUpdatesEnabled={
                          gridPdpPricingUpdatesEnabled
                        }
                      >
                        <StyledPromoPrice
                          amount={promoPriceAmout}
                          $isHighlightedPromo
                          $gridPdpPricingUpdatesEnabled={
                            gridPdpPricingUpdatesEnabled
                          }
                        />
                        <BundleSeparatorMessage>
                          <FormattedMessage
                            id="site_pdp.or"
                            defaultMessage="or"
                          />
                        </BundleSeparatorMessage>
                        <PromoText
                          data-autotag="pdp-non-vip-promo"
                          dangerouslySetInnerHTML={promoText}
                          $gridPdpPricingUpdatesEnabled={
                            gridPdpPricingUpdatesEnabled
                          }
                        />
                      </BundleFirstSetPriceWrapper>
                    )}
                  </BundleFirstSetWrapper>
                </PromoTextWrapper>
              );
            }
          }

          if (shouldShowPromoPrice && !shouldDisplayNewProductPricing) {
            return (
              <PromoTextWrapper
                $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                $isVip={isVip}
              >
                <PromoText
                  data-autotag="pdp-non-vip-promo"
                  dangerouslySetInnerHTML={{
                    __html: pdpLabelForPromoPrice || promos.promo_label,
                  }}
                  $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                />

                <StyledPromoPrice
                  amount={promos.promo_price}
                  pdpSingleCTAFlagEnabled={pdpSingleCTAFlagEnabled}
                  $isHighlightedPromo
                  $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                />
              </PromoTextWrapper>
            );
          }

          if (formattedflags && formattedflags.has('cartpromo')) {
            return (
              <PromoText
                data-autotag="pdp-non-vip-promo"
                dangerouslySetInnerHTML={
                  pdpLabelForPromoPrice
                    ? { __html: pdpLabelForPromoPrice }
                    : null
                }
              />
            );
          }
          return null;
        }}
      </AssetContainer>
    );
  }

  renderVipPrice() {
    const {
      additionalDiscounts,
      hideAfterpay = false,
      isClearanceItem,
      isProductSet,
      isVip,
      product,
      promos,
      shouldShowPromoPrice,
      isGshopActive,
      gridPdpPricingUpdatesEnabled,
    } = this.props;

    const { default_unit_price: defaultPrice } = product;
    const afterpayAmount = shouldShowPromoPrice
      ? promos.promo_price
      : defaultPrice;
    const shouldGreyedOut = shouldShowPromoPrice && isVip;
    let shouldDisplayMemberPrice = true;

    if (gridPdpPricingUpdatesEnabled && !isVip && shouldShowPromoPrice) {
      shouldDisplayMemberPrice = false;
    }

    return (
      <AssetContainer name="pdp_html_fpl" multi>
        {assets => {
          const originalPrice =
            (assets.data && !isProductSet && !isVip && shouldShowPromoPrice) ||
            (isProductSet && shouldShowPromoPrice);
          const PriceComponent = originalPrice ? PriceItem : ProductPriceSpan;

          let hasBundlePromo = false;

          if (assets.data && isVip) {
            if (Array.isArray(assets.data)) {
              assets.data.some(asset => {
                const { flags } = asset.options.customVars ?? {};
                const formattedFlags = toStringSet(flags, true);
                if (formattedFlags.has('bundle')) {
                  hasBundlePromo = true;
                  return true;
                }
                return false;
              });
            } else {
              const { flags } = assets.data.options.customVars ?? {};
              const formattedFlags = toStringSet(flags, true);
              if (formattedFlags.has('bundle')) {
                hasBundlePromo = true;
              }
            }
          }

          const vipSetsPriceLabel = (
            <StyledMessage
              shouldGreyedOut={shouldGreyedOut}
              shouldGreyedOutV2={shouldShowPromoPrice}
            >
              <FormattedMessage
                id="site_pdp.vip_sets_price"
                defaultMessage="VIP:"
              />
            </StyledMessage>
          );
          const vipPriceLabel = isProductSet ? (
            vipSetsPriceLabel
          ) : (
            <StyledMessage
              shouldGreyedOut={shouldGreyedOut}
              shouldGreyedOutV2={shouldShowPromoPrice}
            >
              <FormattedMessage id="site_pdp.vip_price" defaultMessage="VIP:" />
            </StyledMessage>
          );
          if (isGshopActive) {
            return null;
          }

          return (
            <>
              {shouldDisplayMemberPrice && (
                <PriceComponent
                  data-autotag="pdp-vip-price"
                  isMember={!originalPrice}
                  isProductSet={isProductSet}
                  $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
                  $isVip={isVip}
                  $shouldShowPromoStyling={
                    hasBundlePromo ? false : !shouldShowPromoPrice
                  }
                >
                  {vipPriceLabel}
                  <StyledPrice
                    old={shouldShowPromoPrice}
                    amount={defaultPrice}
                    solo={isProductSet}
                    shouldGreyedOut={shouldGreyedOut}
                    shouldGreyedOutV2={shouldShowPromoPrice}
                    data-autotag="pdpVipPricingNumber"
                  />
                </PriceComponent>
              )}

              {!hideAfterpay && (
                <AfterpayMessaging
                  afterpayClassName={afterpayClassName}
                  afterpayAmount={afterpayAmount}
                />
              )}

              {isClearanceItem && additionalDiscounts !== undefined && (
                <StyledAdditionalDiscountText>
                  <FormattedMessage
                    id="site_pdp.additional_sale_applied"
                    defaultMessage="*Discount already applied"
                  />
                </StyledAdditionalDiscountText>
              )}
            </>
          );
        }}
      </AssetContainer>
    );
  }

  renderPriceAmount(product) {
    const { retail_unit_price: retailPrice, default_unit_price: defaultPrice } =
      product;

    const { isBorderfreeCustomer, isProductSet, gridPdpPricingUpdatesEnabled } =
      this.props;

    if (isBorderfreeCustomer) {
      return (
        <ProductPriceSpan
          data-autotag="pdp-reg-price"
          isBorderFreeOrDowngradeUser
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
        >
          <StyledPrice amount={retailPrice} solo />
          {isBorderfreeCustomer && (
            <USDPrice>
              / USD{' '}
              <span>
                $
                {typeof retailPrice === 'number'
                  ? retailPrice.toFixed(2)
                  : retailPrice}
              </span>
            </USDPrice>
          )}
        </ProductPriceSpan>
      );
    } else if (retailPrice === defaultPrice) {
      return this.renderVipPrice();
    } else {
      return (
        <>
          {this.renderVipPrice()}
          {!isProductSet
            ? this.renderRegPrice(retailPrice, defaultPrice)
            : null}
          {isProductSet ? this.renderSetRegPrice() : null}
        </>
      );
    }
  }

  renderRegPrice(retailPrice, memberPrice) {
    const {
      isVip,
      shouldShowPromoPrice,
      isGshopActive,
      gridPdpPricingUpdatesEnabled,
    } = this.props;
    return (
      <PriceItem
        data-autotag="pdp-reg-price"
        $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
      >
        <StyledMessage
          shouldGreyedOut={isVip}
          shouldGreyedOutV2={isVip ? true : shouldShowPromoPrice}
        >
          <FormattedMessage
            id="site_pdp.regular_price"
            defaultMessage="Regular:"
          />
        </StyledMessage>
        <StyledRegPrice
          amount={isGshopActive ? memberPrice : retailPrice}
          old={isVip}
          shouldGreyedOut={isVip}
          shouldGreyedOutV2={gridPdpPricingUpdatesEnabled ? true : isVip}
          data-autotag="pdpRegularPricingNumber"
        />
      </PriceItem>
    );
  }

  renderSetRegPrice() {
    const {
      product,
      isVip,
      shouldShowPromoPrice,
      gridPdpPricingUpdatesEnabled,
    } = this.props;
    const retailPrice = product.retail_unit_price
      ? product.retail_unit_price
      : product.bundle_separate_retail_unit_price;
    return (
      <PriceItem $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}>
        <StyledMessage
          shouldGreyedOut={isVip}
          shouldGreyedOutV2={isVip ? true : shouldShowPromoPrice}
        >
          <FormattedMessage
            id="site_pdp.regular_price"
            defaultMessage="Regular:"
          />
        </StyledMessage>
        <StyledRegPrice
          amount={retailPrice}
          old={isVip}
          shouldGreyedOut={isVip}
          shouldGreyedOutV2={gridPdpPricingUpdatesEnabled ? true : isVip}
        />
      </PriceItem>
    );
  }

  renderPrice() {
    const {
      product,
      isBorderfreeCustomer,
      isVip,
      router,
      shouldDisplayNewProductPricing,
      showPriceSelectors,
      pdpSingleCTAFlagEnabled,
      shouldShowPromoPrice,
      isGshopActive,
      isKlarnaEnabled,
      gridPdpPricingUpdatesEnabled,
    } = this.props;
    const { hidePromo } = this.state;
    const { psrc } = router?.query ?? {};
    // exclude strings starting with 'sale' for NewProductPricing Experiment
    const displayNewProductPricing =
      !/^sale/.test(psrc) && shouldDisplayNewProductPricing;
    const showPromoText =
      !hidePromo && !isBorderfreeCustomer && !isVip && shouldShowPromoPrice;
    if (pdpSingleCTAFlagEnabled && showPriceSelectors) {
      return (
        <PriceContainer
          className={afterpayClassName}
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
        >
          <PriceChooser
            memberPrice={product.default_unit_price}
            promoPrice={product.promos?.promo_price}
            guestPrice={product.retail_unit_price}
            showPromoText={showPromoText}
            renderPromoHeader={this.renderPromoHeader()}
            shouldGreyedOut={showPromoText}
          />
          {isKlarnaEnabled ? (
            <KlarnaMessaging
              baseAmount={
                isVip ? product.default_unit_price : product.promos?.promo_price
              }
            />
          ) : null}
        </PriceContainer>
      );
    }

    return (
      <>
        {!hidePromo && !isBorderfreeCustomer && !displayNewProductPricing && (
          <PromoContainer>{this.renderPromoHeader()}</PromoContainer>
        )}
        <PriceContainer
          className={afterpayClassName}
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
        >
          {displayNewProductPricing ? (
            <ProductPrice
              isGshopActive={isGshopActive}
              memberPrice={product.default_unit_price}
              guestPrice={
                isGshopActive
                  ? product.default_unit_price
                  : product.retail_unit_price
              }
              isPdp
              isVip={isVip}
            />
          ) : (
            this.renderPriceAmount(product)
          )}
          {isKlarnaEnabled ? (
            <KlarnaMessaging
              baseAmount={
                isVip ? product.default_unit_price : product.promos?.promo_price
              }
            />
          ) : null}
        </PriceContainer>
      </>
    );
  }

  render() {
    const {
      className,
      children,
      product,
      shouldHideReviewStars,
      shouldHideFavButton,
      showPromotionalMessage,
      isPreorderable,
      isProductCustomizable,
      isProductSet,
      isKlarnaEnabled,
    } = this.props;

    return (
      <Wrapper className={className} id="productHeader">
        <Desktop>
          <ProductDetailName product={product} isProductFlagHidden />
          {shouldHideFavButton ? null : (
            <StyledWishlistButton
              product={product}
              eventCategory="PDP"
              overlay
              large
            />
          )}
        </Desktop>

        <PriceWrapper
          shouldHideAfterpay={
            isPreorderable || isProductCustomizable || isKlarnaEnabled
          }
        >
          {this.renderPrice()}
        </PriceWrapper>
        {showPromotionalMessage ? (
          <PromotionalMessage>
            <FormattedMessage
              id="site_pdp.promotional_message"
              defaultMessage="This item qualifies for Free Gift with purchase."
            />
          </PromotionalMessage>
        ) : null}
        <Desktop>
          {!isPreorderable && !isProductSet && !shouldHideReviewStars ? (
            <StyledProductDetailStars
              product={product}
              scrollTo="#reviews"
              showReviewCount
              showAverageRating
              starSize={12}
            />
          ) : null}
          <StyledProductDetailFlag product={product} />
        </Desktop>
        {children}
      </Wrapper>
    );
  }
}

function ProductDetailHeaderWithHooks(props) {
  const router = useRouter();
  const {
    singleCTA: { enabled: pdpSingleCTAFlagEnabled, showPriceSelectors },
  } = useProductDetailContext();
  const {
    'additional-payment-methods': additionalPaymentMethods,
    'grid-pdp-pricing-updates': gridPdpPricingUpdatesEnabled,
  } = useLDFlags();
  const isKlarnaEnabled =
    additionalPaymentMethods === 'klarna' ||
    additionalPaymentMethods === 'both';
  const { isGshopActive } = useGshop();
  const { 'pdp-promotional-message': isPromotionalMessageEnabled } =
    useLDFlags();
  return (
    <ProductDetailHeader
      {...props}
      router={router}
      isGshopActive={isGshopActive}
      showPriceSelectors={showPriceSelectors}
      pdpSingleCTAFlagEnabled={pdpSingleCTAFlagEnabled}
      isKlarnaEnabled={isKlarnaEnabled}
      showPromotionalMessage={isPromotionalMessageEnabled}
      gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
    />
  );
}

export default ProductDetailHeaderWithHooks;
